import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { DOCUMENT_DATE_TIME_FORMAT } from '@app/app.constants';
import { SampleUtilsService } from '@app/modules/service-data/services/sample-utils.service';
import { ProductInventoryItemState } from '@app/modules/service-data/store/product-inventory-item.state';
import {
  GetSampleAction,
  GetSamplesListAction,
} from '@app/modules/service-data/store/sample.actions';
import { SampleState } from '@app/modules/service-data/store/sample.state';
import {
  ExportDialogDataType,
  ExportType,
  ExtendedDocument,
  ExtendedProductInventoryItem,
  ProductInventoryItemExtensions,
  ProductInventoryItemStatus,
} from '@app/modules/service-data/types/service-data.types';
import {
  Language,
  Product,
  ProductInventory,
  RuntimeStatus,
} from '@core/core.types';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { DocumentsService } from '@core/services/documents.service';
import { ExportersUtilsService } from '@core/services/exporters-utils.service';
import { ExportersService } from '@core/services/exporters.service';
import { RemoveFromUploadedList } from '@core/store/documents/documents.actions';
import { LanguageState } from '@core/store/languages/languages.state';
import {
  LoadPartnerById,
  LoadPartnersByIds,
} from '@core/store/partners/partners.actions';
import { PartnersState } from '@core/store/partners/partners.state';
import { ProductsListState } from '@core/store/products/products.state';
import {
  ConfirmDocumentApproval,
  MassApproveDocuments,
} from '@core/store/saga/document-approval-saga.actions';
import { DocumentApprovalSagaState } from '@core/store/saga/document-approval-saga.state';
import { generatePassword, Util } from '@core/utils/core.util';
import { HttpUtils } from '@core/utils/http-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ProgressData } from '@shared/components/progress/progress-dialog.component';
import FileSaver from 'file-saver';
import { combineLatest, forkJoin, Observable, Subject } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import {
  debounceTime,
  delay,
  first,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { DocumentApprovalSagaConfirmComponent } from '../components/document-approval-saga-confirm/document-approval-saga-confirm.component';
import { ProductInventoryItemDetailsComponent } from '../components/product-inventory-item/product-inventory-item-details/product-inventory-item-details.component';
import { ProductInventoryItemResultsComponent } from '../components/product-inventory-item/product-inventory-item-results/product-inventory-item-results.component';
import { getFieldSet, ValidatorsHandlerType } from '../service-data.utils';
import {
  GetProductInventoryItemAction,
  GetProductInventoryItemListAction,
} from '../store/product-intentory-item.actions';
import { ProductInventoryItemFilters } from './product-inventory-item.service';

enum Filters {
  DocumentsIds = 'documentIds',
  ReportIds = 'reportIds',
  KitIds = 'kitIds',
  ProductIds = 'productIds',
  Countries = 'countries',
  AccountManagerId = 'accountManagerId',
}

@Injectable({
  providedIn: 'root',
})
export class ProductInventoryItemUtilsService {
  private store = inject(Store);
  private documentsService = inject(DocumentsService);
  private sampleUtilsService = inject(SampleUtilsService);
  private exportersService = inject(ExportersService);
  private exportersUtilsService = inject(ExportersUtilsService);
  private authService = inject(AccountUtilsService);
  private datePipe = inject(DatePipe);
  private ngbModal = inject(NgbModal);
  private translate = inject(TranslateService);

  getExtendedProductInventoryItem(
    pii: ProductInventory,
    appends: ProductInventoryItemExtensions = {
      partner: true,
      product: true,
      sample: null,
    }
  ): Observable<ExtendedProductInventoryItem> {
    if (!pii) {
      return of(null);
    }

    return forkJoin([
      appends.partner
        ? this.store.dispatch(new LoadPartnerById(pii.partnerId))
        : of(null),
      appends.sample
        ? this.store.dispatch(
            new GetSampleAction(
              pii.properties.find(
                (property) => property.key === 'SampleId'
              )?.value,
              false,
              false
            )
          )
        : of(null),
    ]).pipe(
      switchMap(() =>
        combineLatest([
          of(pii),
          appends.product
            ? this.store
                .select(ProductsListState.getProduct)
                .pipe(map((getProduct) => getProduct(pii.productId)))
            : of(null),
          appends.partner
            ? this.store
                .select(PartnersState.getPartnerById)
                .pipe(map((getPartnerById) => getPartnerById(pii.partnerId)))
            : of(null),
          appends.sample
            ? this.store.select(SampleState.getSampleById).pipe(
                map((getSampleById) =>
                  getSampleById(
                    pii.properties.find(
                      (property) => property.key === 'SampleId'
                    )?.value
                  )
                ),
                debounceTime(100),
                switchMap((sample) =>
                  this.sampleUtilsService.getExtendedSample(
                    sample,
                    appends.sample
                  )
                )
              )
            : of(null),
        ])
      ),
      map(([pii, product, partner, sample]) => ({
        ...pii,
        product,
        partner,
        sample,
      }))
    );
  }

  getExtendedProductInventoryItemList(
    piiList: ProductInventory[],
    appends: ProductInventoryItemExtensions = {
      partner: true,
      product: true,
      sample: null,
    }
  ): Observable<ExtendedProductInventoryItem[]> {
    if (!piiList.length) {
      return of([]);
    }

    const partnerIds: string[] = [];
    if (appends.partner) {
      piiList.forEach((pii) => {
        partnerIds.push(pii.partnerId);
      });
    }

    const sampleIds: string[] = [];
    if (appends.sample) {
      piiList.forEach((pii) => {
        sampleIds.push(
          pii.properties.find((property) => property.key === 'SampleId').value
        );
      });
    }

    return this.loadProductInventoryItemConnectedData(piiList, appends).pipe(
      switchMap(() =>
        combineLatest(
          piiList.map((pii) =>
            this.getExtendedProductInventoryItem(pii, appends)
          )
        )
      )
    );
  }

  loadProductInventoryItemConnectedData(
    piiList: ProductInventory[],
    appends: ProductInventoryItemExtensions
  ) {
    if (!piiList.length) {
      return of([]);
    }

    const partnerIds: string[] = [];
    const sampleIds: string[] = [];
    piiList.forEach((pii) => {
      partnerIds.push(pii.partnerId);
      sampleIds.push(
        pii.properties.find((property) => property.key === 'SampleId')?.value
      );
    });

    return forkJoin([
      appends.sample
        ? this.store
            .dispatch(
              new GetSamplesListAction({ ids: sampleIds }, {}, null, false)
            )
            .pipe(
              switchMap(() =>
                this.store
                  .select(SampleState.getSamplesByIds)
                  .pipe(map((getByIds) => getByIds(sampleIds)))
              ),
              first(),
              switchMap((samples) =>
                this.sampleUtilsService.loadSampleConnectedData(
                  samples,
                  appends.sample
                )
              )
            )
        : of(null),
      appends.partner
        ? this.store.dispatch(new LoadPartnersByIds(partnerIds))
        : of(null),
    ]);
  }

  getPiisDataWithPartnerLanguage(
    piis: ExtendedProductInventoryItem[]
  ): Observable<[ExtendedProductInventoryItem, string][]> {
    if (!piis.length) {
      return of([]);
    }
    return forkJoin(
      piis.map((pii) =>
        this.store.select(LanguageState.getLanguageById).pipe(
          map((filterById) => filterById(pii.partner.languageIds[0])),
          first((lng: Language) => !!lng),
          map((language: Language) => {
            return [pii, language.isoCode] as [
              ExtendedProductInventoryItem,
              string,
            ];
          })
        )
      )
    );
  }

  calculatePiiProgress(pii: ProductInventory) {
    if (
      pii.lastStatus === ProductInventoryItemStatus.processingFinished ||
      pii.lastStatus === ProductInventoryItemStatus.canceled ||
      pii.lastStatus === ProductInventoryItemStatus.processingFailed
    ) {
      return 3;
    } else if (pii.documents?.length > 0 || pii.reports?.length > 0) {
      return 2;
    }
    if (pii.lastStatus === ProductInventoryItemStatus.processingStarted) {
      return 1;
    }
    return 0;
  }

  public openDetails(piiId: string) {
    const modalRef = this.ngbModal.open(ProductInventoryItemDetailsComponent, {
      centered: true,
      modalDialogClass: 'modal-size-xxl',
    });

    modalRef.componentInstance.piiId = piiId;
  }

  public openResults(piiIds: string[]) {
    const modalRef = this.ngbModal.open(ProductInventoryItemResultsComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.piiIds = piiIds;
  }

  buildHttpParams(params: ProductInventoryItemFilters) {
    let httpParams = HttpUtils.buildHttpParams(params);

    if (params.documentIds?.length) {
      httpParams = httpParams.set(
        Filters.DocumentsIds,
        params.documentIds.join(',')
      );
    }

    if (params.reportIds?.length) {
      httpParams = httpParams.set(
        Filters.ReportIds,
        params.reportIds.join(',')
      );
    }

    if (params.kitIds?.length) {
      httpParams = httpParams.set(
        Filters.KitIds,
        Array.isArray(params.kitIds) ? params.kitIds.join(',') : params.kitIds
      );
    }

    if (params.productIds?.length) {
      httpParams = httpParams.set(
        Filters.ProductIds,
        params.productIds.join(',')
      );
    }

    if (params.countries?.length) {
      httpParams = httpParams.set(
        Filters.Countries,
        params.countries.join(',')
      );
    }

    if (params.accountManagerId?.length) {
      httpParams = httpParams.set(
        Filters.AccountManagerId,
        params.accountManagerId.join(',')
      );
    }

    return httpParams;
  }

  public niptExportRequestForms(ids: string[]): Observable<boolean> {
    const password = generatePassword();

    const progressSubject = this.exportersUtilsService.openProgressDialog(
      'pii.export.types.RequestForms',
      password
    );

    if (!ids) {
      return of(false);
    }

    return this.exportRequestForms(ids, progressSubject, password);
  }

  public exportSelected(
    exportType: ExportType,
    ids?: string[]
  ): Observable<string> {
    const password =
      exportType === ExportType.Insurance ||
      exportType === ExportType.RequestForms
        ? generatePassword()
        : undefined;

    const progressSubject = this.exportersUtilsService.openProgressDialog(
      this.translate.instant(
        'pii.export.types.' +
          Object.keys(ExportType)[Object.values(ExportType).indexOf(exportType)]
      ),
      password
    );

    if (exportType === ExportType.Report) {
      return this.authService
        .hasRequiredPermission$('pii.export.reportExport.notAnonymous')
        .pipe(
          first(),
          switchMap((anonymous: boolean) =>
            this.exportersUtilsService.exportSelected(
              ExportDialogDataType.Pii,
              ids,
              exportType,
              password,
              anonymous
            )
          ),
          tap((status: string) => {
            if (status !== RuntimeStatus.Running) {
              progressSubject.next({
                value: 100,
                status: 'shared.progress.done',
                success: status === RuntimeStatus.Completed ? true : false,
              });
            }
          })
        );
    }

    return this.exportersUtilsService
      .exportSelected(ExportDialogDataType.Pii, ids, exportType, password)
      .pipe(
        tap((status: string) => {
          if (status !== RuntimeStatus.Running) {
            progressSubject.next({
              value: 100,
              status: 'shared.progress.done',
              success: status === RuntimeStatus.Completed ? true : false,
            });
          }
        })
      );
  }

  public export(
    exportType: ExportType,
    filters?: { [key: string]: string }
  ): Observable<string> {
    const password =
      exportType === ExportType.Insurance ||
      exportType === ExportType.RequestForms
        ? generatePassword()
        : undefined;

    const progressSubject = this.exportersUtilsService.openProgressDialog(
      this.translate.instant(
        'pii.export.types.' +
          Object.keys(ExportType)[Object.values(ExportType).indexOf(exportType)]
      ),
      password
    );

    if (exportType === ExportType.Report) {
      return this.authService
        .hasRequiredPermission$('pii.export.reportExport.notAnonymous')
        .pipe(
          first(),
          switchMap((anonymous: boolean) =>
            this.exportersUtilsService.export(
              filters,
              exportType,
              password,
              anonymous
            )
          ),
          tap((status: string) => {
            if (status !== RuntimeStatus.Running) {
              progressSubject.next({
                value: 100,
                status: 'Done',
                success: status === RuntimeStatus.Completed ? true : false,
              });
            }
          })
        );
    }

    return this.exportersUtilsService
      .export(filters, exportType, password)
      .pipe(
        tap((status: string) => {
          if (status !== RuntimeStatus.Running) {
            progressSubject.next({
              value: 100,
              status: 'Done',
              success: status === RuntimeStatus.Completed ? true : false,
            });
          }
        })
      );
  }

  public replaceFilenameBarcode(fileName, kitId) {
    const fileNameSplit = fileName.split('-');
    fileNameSplit[0] = kitId;
    return fileNameSplit.join('-');
  }

  public massApproveDocuments(documents: ExtendedDocument[]) {
    const sagaId = Util.CreateGuid();

    const documentIds = documents.map(
      (document: ExtendedDocument) => document.id
    );

    return this.store
      .dispatch(new MassApproveDocuments(documentIds, sagaId))
      .pipe(
        tap(() => console.log('mass approve reports dispatched')),
        switchMap(() =>
          this.store.select(DocumentApprovalSagaState.isSagaReadyForApproval)
        ),
        first((isSagaReadyForApproval) => isSagaReadyForApproval(sagaId)),
        switchMap(() =>
          this.store.select(DocumentApprovalSagaState.hasSagaErrors)
        ),
        first(),
        switchMap((hasSagaErrors) => {
          if (!hasSagaErrors(sagaId)) {
            return this.store.dispatch(new ConfirmDocumentApproval(sagaId));
          } else {
            return this.store
              .select(DocumentApprovalSagaState.getSagaById)
              .pipe(
                first(),
                switchMap((getSagaById) => {
                  const modalRef = this.ngbModal.open(
                    DocumentApprovalSagaConfirmComponent,
                    {
                      centered: true,
                    }
                  );

                  modalRef.componentInstance.state = getSagaById(sagaId).state;

                  return modalRef.closed.pipe(
                    switchMap((result) => {
                      if (result) {
                        return this.store
                          .dispatch(new ConfirmDocumentApproval(sagaId))
                          .pipe(map(() => result));
                      } else {
                        return of(null);
                      }
                    })
                  );
                })
              );
          }
        }),
        switchMap((result) => {
          if (!result) {
            return of(null);
          }

          return this.store
            .select(DocumentApprovalSagaState.isSagaInProgress)
            .pipe(
              first((isSagaInProgress) => !isSagaInProgress(sagaId)),
              switchMap(() =>
                forkJoin([
                  this.store.dispatch(new RemoveFromUploadedList(documentIds)),
                  ...documents.map((document: ExtendedDocument) =>
                    this.store.dispatch(
                      new GetProductInventoryItemAction(
                        document.productInventoryItem.id,
                        true,
                        true
                      )
                    )
                  ),
                ])
              )
            );
        })
      );
  }

  private exportRequestForms(
    piiIds: string[],
    progressSubject: Subject<ProgressData> = new Subject(),
    password = ''
  ): Observable<boolean> {
    if (!piiIds && !piiIds.length) {
      return of(false);
    }

    const expectedFiles = piiIds.length + 1;

    progressSubject.next({
      value: Math.round((1 / expectedFiles) * 100),
      status: 'pii.export.progress.exportingRequestForms',
    });

    return this.store
      .dispatch(
        new GetProductInventoryItemListAction({ ids: piiIds }, null, true)
      )
      .pipe(
        delay(300),
        switchMap(() =>
          this.store
            .select(ProductInventoryItemState.getProductInventoryItemsByIds)
            .pipe(
              first(),
              map((filterByIds) => filterByIds(piiIds)),
              switchMap((piis: ProductInventory[]) =>
                this.getExtendedProductInventoryItemList(piis, {
                  product: true,
                  sample: { doctor: true, serviceData: { bundles: true } },
                })
              ),
              first(),
              switchMap((piis: ExtendedProductInventoryItem[]) =>
                combineLatest([
                  ...this.getRequestForms(piis, progressSubject, expectedFiles),
                ]).pipe(
                  switchMap(
                    (
                      requestForms: {
                        content: string;
                        filename: string;
                      }[]
                    ) => {
                      if (requestForms && requestForms.length) {
                        return this.exportersService.generateRequestForm(
                          requestForms,
                          password
                        );
                      }
                    }
                  )
                )
              )
            )
        ),
        map((zip: { data: string }) => {
          const dateString = this.datePipe.transform(
            new Date(),
            DOCUMENT_DATE_TIME_FORMAT
          );

          FileSaver.saveAs(
            new Blob([Util.ConvertBase64ToBlob(zip?.data)]),
            `export ${dateString}.zip`
          );

          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: true,
          });

          return true;
        })
      );
  }

  private getRequestForms(
    piis: ExtendedProductInventoryItem[],
    progressSubject: Subject<ProgressData>,
    expectedFiles: number
  ): Observable<{
    content: string;
    filename: string;
  }>[] {
    return piis.map((pii: ExtendedProductInventoryItem, index: number) =>
      this.store.select(ProductsListState.getAllChildProducts).pipe(
        map((getByIds) => getByIds([pii.productId])),
        switchMap((products: Product[]) => {
          const selectedDataInputTypes = products.map(
            (product) => product.dataInputType
          );
          const fieldSet = getFieldSet(
            selectedDataInputTypes,
            ValidatorsHandlerType.SERVICEDATA
          );
          const sampleFieldSet = getFieldSet(
            selectedDataInputTypes,
            ValidatorsHandlerType.SAMPLE
          );

          if (pii.sample?.previousSampleId) {
            return this.getPreviousKitId(pii.sample.previousSampleId).pipe(
              map((previousSampleKitId: string) => {
                const requestForm =
                  this.exportersUtilsService.getRequestFormTemplate(
                    pii,
                    previousSampleKitId,
                    { index },
                    selectedDataInputTypes,
                    fieldSet,
                    sampleFieldSet
                  );

                progressSubject.next({
                  value: Math.round(((index + 1) / expectedFiles) * 100),
                  status: 'pii.export.progress.exportingRequestForms',
                });

                return requestForm;
              })
            );
          } else {
            const requestForm =
              this.exportersUtilsService.getRequestFormTemplate(
                pii,
                null,
                { index },
                selectedDataInputTypes,
                fieldSet,
                sampleFieldSet
              );

            progressSubject.next({
              value: Math.round(((index + 1) / expectedFiles) * 100),
              status: 'pii.export.progress.exportingRequestForms',
            });

            return of(requestForm);
          }
        })
      )
    );
  }

  private getPreviousKitId(previousSampleId: string): Observable<string> {
    return this.store.dispatch(new GetSampleAction(previousSampleId)).pipe(
      switchMap(() =>
        this.store.select(SampleState.getSampleById).pipe(
          map((getSampleById) => {
            return getSampleById(previousSampleId)?.kitId;
          })
        )
      )
    );
  }
}
